import { APP_SETTINGS } from './config/appSettings'

export const APP_UPDATE_READY_EVENT = 'minimany:update-ready'
export const APP_UPDATE_AVAILABLE_EVENT = 'minimany:update-available'
export const SERVICE_WORKER_BUILD_REQUEST = 'MINIMANY_GET_BUILD_ID'
export const SERVICE_WORKER_BUILD_RESPONSE = 'MINIMANY_BUILD_ID'
export const SERVICE_WORKER_NAVIGATE_REQUEST = 'MINIMANY_NAVIGATE_CLIENT'
export const SERVICE_WORKER_LINK_PREVIEW_IMAGE_CACHE_STATS_REQUEST = 'MINIMANY_GET_LINK_PREVIEW_IMAGE_CACHE_STATS'
export const SERVICE_WORKER_LINK_PREVIEW_IMAGE_CACHE_STATS_RESPONSE = 'MINIMANY_LINK_PREVIEW_IMAGE_CACHE_STATS'

const APP_SHELL_CACHE_PREFIX = 'minimany-shell-'
const APP_UPDATE_REMINDER_STORAGE_KEY = 'minimany:app-update-reminder:v1'

const DEFAULT_CHECK_INTERVAL_MS = APP_SETTINGS.appUpdates.checkIntervalMs
const DEFAULT_MINIMUM_CHECK_GAP_MS = APP_SETTINGS.appUpdates.minimumCheckGapMs

interface UpdateCheckOptions {
  windowTarget?: EventTarget
  documentTarget?: EventTarget & { visibilityState: DocumentVisibilityState }
  isOnline?: () => boolean
  now?: () => number
  fetcher?: typeof fetch
  setInterval?: (callback: () => void, delay: number) => number
  clearInterval?: (timer: number) => void
  checkIntervalMs?: number
  minimumCheckGapMs?: number
  onChecked?: () => void
}

export interface ServiceWorkerUpdateLifecycle {
  initialCheck: Promise<boolean>
  check: (force?: boolean) => Promise<boolean>
  dispose: () => void
}

export interface AppReloadRequest {
  refreshShell: boolean
  deployedBuildId: string | null
}

export interface LinkPreviewImageCacheStats {
  entryCount: number
  byteCount: number
  maxEntries: number
}

interface AppShellCacheStorage {
  keys: () => Promise<string[]>
  delete: (cacheName: string) => Promise<boolean>
}

interface AppUpdateReminderStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem?: (key: string) => void
}

interface ServiceWorkerActivationOptions {
  timeoutMs?: number
}

function defaultReminderStorage(): AppUpdateReminderStorage | null {
  return typeof localStorage === 'undefined' ? null : localStorage
}

export function startServiceWorkerUpdateChecks(
  registration: ServiceWorkerRegistration,
  serviceWorkerUrl: string,
  options: UpdateCheckOptions = {},
): ServiceWorkerUpdateLifecycle {
  const windowTarget = options.windowTarget ?? window
  const documentTarget = options.documentTarget ?? document
  const isOnline = options.isOnline ?? (() => navigator.onLine)
  const now = options.now ?? Date.now
  const fetcher = options.fetcher ?? fetch
  const scheduleInterval = options.setInterval ?? window.setInterval.bind(window)
  const cancelInterval = options.clearInterval ?? window.clearInterval.bind(window)
  const checkIntervalMs = options.checkIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS
  const minimumCheckGapMs = options.minimumCheckGapMs ?? DEFAULT_MINIMUM_CHECK_GAP_MS
  let lastCheckAt = Number.NEGATIVE_INFINITY
  let activeCheck: Promise<boolean> | null = null

  const check = (force = false): Promise<boolean> => {
    if (activeCheck) return activeCheck
    if (!isOnline() || documentTarget.visibilityState !== 'visible' || registration.installing) {
      return Promise.resolve(false)
    }
    const checkedAt = now()
    if (!force && checkedAt - lastCheckAt < minimumCheckGapMs) return Promise.resolve(false)
    lastCheckAt = checkedAt
    activeCheck = (async () => {
      try {
        const response = await fetcher(serviceWorkerUrl, {
          cache: 'no-store',
          headers: {
            'cache': 'no-store',
            'cache-control': 'no-cache',
          },
        })
        if (!response.ok) return false
        await registration.update()
        options.onChecked?.()
        return true
      } catch {
        return false
      } finally {
        activeCheck = null
      }
    })()
    return activeCheck
  }

  const checkWhenActive = () => { void check() }
  const interval = scheduleInterval(checkWhenActive, checkIntervalMs)
  windowTarget.addEventListener('online', checkWhenActive)
  windowTarget.addEventListener('focus', checkWhenActive)
  windowTarget.addEventListener('pageshow', checkWhenActive)
  documentTarget.addEventListener('visibilitychange', checkWhenActive)

  return {
    initialCheck: check(true),
    check,
    dispose: () => {
      cancelInterval(interval)
      windowTarget.removeEventListener('online', checkWhenActive)
      windowTarget.removeEventListener('focus', checkWhenActive)
      windowTarget.removeEventListener('pageshow', checkWhenActive)
      documentTarget.removeEventListener('visibilitychange', checkWhenActive)
    },
  }
}

export function readAppUpdateReminderAt(
  storage: AppUpdateReminderStorage | null = defaultReminderStorage(),
): number {
  if (!storage) return 0
  try {
    const value = Number(storage.getItem(APP_UPDATE_REMINDER_STORAGE_KEY))
    return Number.isFinite(value) && value > 0 ? value : 0
  } catch {
    return 0
  }
}

export function snoozeAppUpdateReminder(
  storage: AppUpdateReminderStorage | null = defaultReminderStorage(),
  now = Date.now(),
): number {
  const remindAt = now + APP_SETTINGS.appUpdates.reminderSnoozeMs
  try {
    storage?.setItem(APP_UPDATE_REMINDER_STORAGE_KEY, String(remindAt))
  } catch {
    // An in-memory reminder still applies for this session when storage is unavailable.
  }
  return remindAt
}

export function shouldPromptForAppUpdate(
  storage: AppUpdateReminderStorage | null = defaultReminderStorage(),
  now = Date.now(),
): boolean {
  return readAppUpdateReminderAt(storage) <= now
}

export function clearAppUpdateReminder(
  storage: AppUpdateReminderStorage | null = defaultReminderStorage(),
): void {
  try {
    if (storage?.removeItem) storage.removeItem(APP_UPDATE_REMINDER_STORAGE_KEY)
    else storage?.setItem(APP_UPDATE_REMINDER_STORAGE_KEY, '0')
  } catch {
    // The reminder is only a convenience preference; update safety does not depend on it.
  }
}

export function dispatchSafeAppReload(
  target: EventTarget = window,
  reload: () => void = () => window.location.reload(),
  request: AppReloadRequest = { refreshShell: false, deployedBuildId: null },
): boolean {
  const reloadImmediately = target.dispatchEvent(new CustomEvent<AppReloadRequest>(APP_UPDATE_READY_EVENT, {
    cancelable: true,
    detail: request,
  }))
  if (reloadImmediately) reload()
  return reloadImmediately
}

export function requestAppNavigationThroughWorker(
  worker: ServiceWorker,
  url: string,
): boolean {
  try {
    worker.postMessage({ type: SERVICE_WORKER_NAVIGATE_REQUEST, url })
    return true
  } catch {
    return false
  }
}

export function readAppReloadRequest(event: Event): AppReloadRequest {
  if (
    'detail' in event
    && typeof event.detail === 'object'
    && event.detail !== null
    && 'refreshShell' in event.detail
    && event.detail.refreshShell === true
  ) return {
    refreshShell: true,
    deployedBuildId: 'deployedBuildId' in event.detail
      && typeof event.detail.deployedBuildId === 'string'
      ? event.detail.deployedBuildId
      : null,
  }
  return { refreshShell: false, deployedBuildId: null }
}

export function dispatchAppUpdateAvailable(
  buildId: string,
  target: EventTarget = window,
): void {
  target.dispatchEvent(new CustomEvent(APP_UPDATE_AVAILABLE_EVENT, {
    detail: { buildId },
  }))
}

export async function clearAppShellCaches(
  cacheStorage: AppShellCacheStorage = caches,
  preserveBuildId: string | null = null,
): Promise<void> {
  const names = await cacheStorage.keys()
  const preservedCacheName = preserveBuildId
    ? `${APP_SHELL_CACHE_PREFIX}${preserveBuildId}`
    : null
  await Promise.all(
    names
      .filter((name) => (
        name.startsWith(APP_SHELL_CACHE_PREFIX)
        && name !== preservedCacheName
      ))
      .map((name) => cacheStorage.delete(name)),
  )
}

export async function verifyFreshAppShell(
  deployedBuildId: string,
  fetcher: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await fetcher(
      `/index.html?minimany-update=${encodeURIComponent(deployedBuildId)}`,
      {
        cache: 'no-store',
        headers: {
          'cache': 'no-store',
          'cache-control': 'no-cache',
        },
      },
    )
    return response.ok && response.headers.get('content-type')?.includes('text/html') === true
  } catch {
    return false
  }
}

export async function safelyReloadAfterLocalDurability(
  persistCurrentState: () => Promise<void>,
  drainLocalQueues: () => Promise<void>,
  reload: () => void,
): Promise<void> {
  await persistCurrentState()
  await drainLocalQueues()
  reload()
}

export async function activateWaitingServiceWorker(
  registration: ServiceWorkerRegistration,
  deployedBuildId: string,
  options: ServiceWorkerActivationOptions = {},
): Promise<boolean> {
  const timeoutMs = options.timeoutMs ?? APP_SETTINGS.appUpdates.activationTimeoutMs

  try {
    await registration.update()
  } catch {
    return false
  }

  return new Promise((resolve) => {
    let settled = false
    let activationRequested = false
    let observedWorker: ServiceWorker | null = null

    const finish = (activated: boolean) => {
      if (settled) return
      settled = true
      globalThis.clearTimeout(timer)
      registration.removeEventListener('updatefound', inspect)
      observedWorker?.removeEventListener('statechange', inspect)
      resolve(activated)
    }

    const inspectActiveWorker = () => {
      void queryWorkerBuildId(registration.active).then((buildId) => {
        if (buildId === deployedBuildId) finish(true)
      })
    }

    const inspect = () => {
      if (settled) return
      const lifecycleWorker = registration.waiting ?? registration.installing
      if (lifecycleWorker !== observedWorker) {
        observedWorker?.removeEventListener('statechange', inspect)
        observedWorker = lifecycleWorker
        observedWorker?.addEventListener('statechange', inspect)
      }
      inspectActiveWorker()

      const waiting = registration.waiting
      if (!waiting || activationRequested) return
      activationRequested = true
      void queryWorkerBuildId(waiting).then((buildId) => {
        if (settled) return
        if (buildId !== deployedBuildId) {
          finish(false)
          return
        }
        waiting.postMessage({ type: 'SKIP_WAITING' })
        inspectActiveWorker()
      })
    }

    const timer = globalThis.setTimeout(() => finish(false), timeoutMs)
    registration.addEventListener('updatefound', inspect)
    inspect()
  })
}

export function queryWorkerBuildId(
  worker: ServiceWorker | null,
  timeoutMs = APP_SETTINGS.appUpdates.workerBuildQueryTimeoutMs,
): Promise<string | null> {
  if (!worker || typeof MessageChannel === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    const finish = (buildId: string | null) => {
      globalThis.clearTimeout(timer)
      channel.port1.close()
      channel.port2.close()
      resolve(buildId)
    }
    const timer = globalThis.setTimeout(() => finish(null), timeoutMs)
    channel.port1.onmessage = (event: MessageEvent<unknown>) => {
      const data = event.data
      if (
        typeof data === 'object'
        && data !== null
        && 'type' in data
        && data.type === SERVICE_WORKER_BUILD_RESPONSE
        && 'buildId' in data
        && typeof data.buildId === 'string'
      ) finish(data.buildId)
    }
    try {
      worker.postMessage({ type: SERVICE_WORKER_BUILD_REQUEST }, [channel.port2])
    } catch {
      finish(null)
    }
  })
}

export function queryLinkPreviewImageCacheStats(
  worker: ServiceWorker | null,
  timeoutMs = APP_SETTINGS.links.previewImageCacheStatsQueryTimeoutMs,
): Promise<LinkPreviewImageCacheStats | null> {
  if (!worker || typeof MessageChannel === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    const finish = (stats: LinkPreviewImageCacheStats | null) => {
      globalThis.clearTimeout(timer)
      channel.port1.close()
      channel.port2.close()
      resolve(stats)
    }
    const timer = globalThis.setTimeout(() => finish(null), timeoutMs)
    channel.port1.onmessage = (event: MessageEvent<unknown>) => {
      const data = event.data
      if (
        typeof data === 'object'
        && data !== null
        && 'type' in data
        && data.type === SERVICE_WORKER_LINK_PREVIEW_IMAGE_CACHE_STATS_RESPONSE
        && 'entryCount' in data
        && 'byteCount' in data
        && 'maxEntries' in data
        && typeof data.entryCount === 'number'
        && Number.isFinite(data.entryCount)
        && typeof data.byteCount === 'number'
        && Number.isFinite(data.byteCount)
        && typeof data.maxEntries === 'number'
        && Number.isFinite(data.maxEntries)
      ) {
        finish({
          entryCount: Math.max(0, Math.floor(data.entryCount)),
          byteCount: Math.max(0, Math.floor(data.byteCount)),
          maxEntries: Math.max(0, Math.floor(data.maxEntries)),
        })
      }
    }
    try {
      worker.postMessage({ type: SERVICE_WORKER_LINK_PREVIEW_IMAGE_CACHE_STATS_REQUEST }, [channel.port2])
    } catch {
      finish(null)
    }
  })
}

export async function queryDeployedBuildId(
  fetcher: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const response = await fetcher('/version.json', {
      cache: 'no-store',
      headers: {
        'cache': 'no-store',
        'cache-control': 'no-cache',
      },
    })
    if (!response.ok) return null
    const value = await response.json() as unknown
    return typeof value === 'object'
      && value !== null
      && 'buildId' in value
      && typeof value.buildId === 'string'
      ? value.buildId
      : null
  } catch {
    return null
  }
}

export function stalePageIsControlledByDeployedWorker(
  pageBuildId: string,
  deployedBuildId: string | null,
  workerBuildId: string | null,
): boolean {
  return Boolean(
    deployedBuildId
    && deployedBuildId !== pageBuildId
    && workerBuildId === deployedBuildId,
  )
}
